<script>
// Generic recommendation card, rendered per item.type (member/exhibitor/session).
// Relative imports only -- this component is also bundled into speaker.bundle
// (webpack speaker) where the "@/" alias does not exist.
import ChannelPrivateButton from '../channel/channel-private-button.vue'
import AppFavorite from '../lib/favorite.vue'

export default {
  name: 'RecCard',
  components: { ChannelPrivateButton, AppFavorite },
  props: {
    item: { type: Object, required: true },
  },

  computed: {
    // Show at most two "passt zu" chips; the rest collapse into a "+N" chip.
    visibleWhy() {
      return (this.item.why || []).slice(0, 2)
    },
    extraWhy() {
      const why = this.item.why || []
      return why.length > 2 ? why.length - 2 : 0
    },
    // 'visitor' items (exhibitor-side recommendations) render like members:
    // same fields, same connect CTA.
    isMember() { return this.item.type === 'member' || this.item.type === 'visitor' },
    isSession() { return this.item.type === 'session' },
    isExhibitor() { return this.item.type === 'exhibitor' },
  },

  methods: {
    openItem() {
      const url = this.item.url || ''
      if (!url) return
      // In the CMS embed ($WebApp) exhibitor/session links are full Django URLs
      // that the embed router cannot resolve -> do a real navigation to the
      // existing Django page. Member links (/community/:uuid) resolve via the
      // embed's default routes, so route them internally in both contexts.
      // In the SPA the redirect routes normalize get_absolute_url() paths.
      if (this.$WebApp && !this.isMember) {
        window.location.href = url
      } else {
        this.$router.push(url).catch(() => {})
      }
    },

    parseDate(datetimeStr, locale = 'de') {
      const opts = { weekday: 'short', day: 'numeric', month: 'short',
                     timeZone: 'Europe/Berlin' }
      return new Date(datetimeStr).toLocaleString(locale, opts)
    },
    displayTime(datetimeStr, locale = 'de') {
      const opts = { hour: 'numeric', minute: 'numeric', timeZone: 'Europe/Berlin' }
      return new Date(datetimeStr).toLocaleString(locale, opts)
    },
  },
}
</script>

<template>
  <div class="list-group-item">

    <!-- Member: avatar + name/company; content -> profile, action = Verbinden -->
    <div v-if="isMember" class="d-flex flex-row align-items-start">
      <div class="d-flex flex-row align-items-start flex-fill rc-clickable" @click.prevent="openItem">
        <img v-if="item.image" :src="item.image"
         class="rc-thumb rounded-circle object-fit-cover flex-shrink-0 me-2" />
        <div v-else class="rc-thumb rounded-circle bg-light text-secondary d-flex align-items-center justify-content-center flex-shrink-0 me-2">
          <i class="fas fa-user"></i>
        </div>
        <div class="flex-fill min-w-0">
          <h4 class="card-title mb-0">
            {{ item.title }}
            <!-- Fairs the visitor holds tickets for (exhibitor-side items) -->
            <span v-for="sn in item.events || []" :key="sn"
             class="badge bg-light text-dark border rc-event-badge ms-1">{{ sn }}</span>
          </h4>
          <div v-if="item.company_name" class="text-muted"><small>{{ item.company_name }}</small></div>
          <div v-if="visibleWhy.length" class="mt-1 d-flex flex-wrap gap-1">
            <span class="text-muted align-self-center"><small>passt zu:</small></span>
            <span v-for="name in visibleWhy" :key="name"
             class="badge bg-light text-dark border fw-normal badge-wrap">{{ name }}</span>
            <span v-if="extraWhy" class="badge bg-light text-dark border fw-normal badge-wrap">+{{ extraWhy }}</span>
          </div>
        </div>
      </div>
      <div class="align-self-center ms-2" @click.stop>
        <channel-private-button :contact-id="item.uuid" compact />
      </div>
    </div>

    <!-- Exhibitor: logo + name + slogan; content navigates, action = Vormerken -->
    <div v-else-if="isExhibitor" class="d-flex flex-row align-items-start">
      <div class="d-flex flex-row align-items-start flex-fill rc-clickable" @click.prevent="openItem">
        <img v-if="item.logo" :src="item.logo"
         class="rc-thumb rounded object-fit-contain bg-white flex-shrink-0 me-2" />
        <div v-else class="rc-thumb rounded bg-light text-secondary d-flex align-items-center justify-content-center flex-shrink-0 me-2">
          <i class="fas fa-store"></i>
        </div>
        <div class="flex-fill min-w-0">
          <h4 class="card-title mb-0">{{ item.title }}</h4>
          <div v-if="item.text" class="text-muted"><small class="rc-clamp-2">{{ item.text }}</small></div>
          <div v-if="visibleWhy.length" class="mt-1 d-flex flex-wrap gap-1">
            <span class="text-muted align-self-center"><small>passt zu:</small></span>
            <span v-for="name in visibleWhy" :key="name"
             class="badge bg-light text-dark border fw-normal badge-wrap">{{ name }}</span>
            <span v-if="extraWhy" class="badge bg-light text-dark border fw-normal badge-wrap">+{{ extraWhy }}</span>
          </div>
        </div>
      </div>
      <div class="align-self-center ms-2" @click.stop>
        <app-favorite v-if="item.sfid" attr="exhibitor" :value="item.sfid" :obj="item" button />
        <i v-else class="fas fa-chevron-right text-muted rc-clickable" @click.prevent="openItem"></i>
      </div>
    </div>

    <!-- Session: date badge + title; content navigates, action = Vormerken -->
    <div v-else-if="isSession" class="d-flex flex-row align-items-start">
      <div class="d-flex flex-row align-items-start flex-fill rc-clickable" @click.prevent="openItem">
        <div class="rc-date flex-shrink-0 bg-light rounded text-center p-1 lh-1 d-none d-sm-block me-2">
          <template v-if="item.start_datetime">
            <div class="small fw-semibold">{{ parseDate(item.start_datetime) }}</div>
            <div class="small">{{ displayTime(item.start_datetime) }}</div>
          </template>
          <i v-else class="fas fa-calendar-day"></i>
        </div>
        <div class="flex-fill min-w-0">
          <h4 class="card-title mb-0 fs-6 lh-sm rc-clamp-2">{{ item.title }}</h4>
          <div v-if="item.text" class="text-muted"><small>{{ item.text }}</small></div>
          <div v-if="visibleWhy.length" class="mt-1 d-flex flex-wrap gap-1">
            <span class="text-muted align-self-center"><small>passt zu:</small></span>
            <span v-for="name in visibleWhy" :key="name"
             class="badge bg-light text-dark border fw-normal badge-wrap">{{ name }}</span>
            <span v-if="extraWhy" class="badge bg-light text-dark border fw-normal badge-wrap">+{{ extraWhy }}</span>
          </div>
        </div>
      </div>
      <div class="align-self-center ms-2" @click.stop>
        <app-favorite v-if="item.content_code" attr="session" :value="item.content_code" :obj="item" button />
        <i v-else class="fas fa-chevron-right text-muted rc-clickable" @click.prevent="openItem"></i>
      </div>
    </div>

  </div>
</template>

<style scoped>
/* Only what Bootstrap 5 has no utility for: fixed thumb/date sizes,
   two-line clamp, and a pointer cursor. Everything else uses BS utilities. */
.rc-thumb { width: 44px; height: 44px; }
.rc-date { width: 68px; }
.rc-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rc-clickable { cursor: pointer; }
.rc-event-badge {
  font-size: .62rem;
  font-weight: normal;
  vertical-align: middle;
}
</style>
